'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Box, Container, Typography, Grid, Card, CardContent, Button, Skeleton, alpha, IconButton } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { fetchArticles, Article, ArticleCategory } from '@/libs/getArticles'

interface HomeArticlesProps {
  initialArticles: Article[]
  categories: ArticleCategory[]
  urlAPI: string
}

// Brand guidelines / Luxury color palette
const BRAND = '#735626'
const BRAND_HOVER = '#5a421b'
const BRAND_LIGHT = '#fdfbf7'
const BRAND_PILL_BG = '#f4ede2'
const CHARCOAL = '#1c150c'
const MUTED_BROWN = '#736551'
const WARM_BG = '#faf7f2'

export default function HomeArticles({ initialArticles, categories, urlAPI }: HomeArticlesProps) {
  const [selectedCategory, setSelectedCategory] = useState<number | string>('all')
  const [articles, setArticles] = useState<Article[]>(initialArticles)
  const [loading, setLoading] = useState<boolean>(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let active = true

    if (selectedCategory === 'all') {
      setArticles(initialArticles)
      return
    }

    const loadCategoryArticles = async () => {
      setLoading(true)
      const catId = Number(selectedCategory)
      const res = await fetchArticles(urlAPI, {
        id_kategori: catId,
        halaman: 0,
        baris: 6
      })
      if (active) {
        setArticles(res.list || [])
        setLoading(false)
      }
    }

    loadCategoryArticles()
    return () => {
      active = false
    }
  }, [selectedCategory, initialArticles, urlAPI])

  const getArticleImageUrl = (gambarJson?: string) => {
    if (!gambarJson) return '/images/icon/NoData.png'
    try {
      const images = JSON.parse(gambarJson)
      if (Array.isArray(images) && images.length > 0) {
        const filename = images[0]
        const baseRegisUrl = process.env.NEXT_PUBLIC_URL_IMAGE_REGIS || ''
        if (baseRegisUrl.endsWith('rumahduka/')) {
          return baseRegisUrl.slice(0, -10) + 'artikel/' + filename
        }
        return baseRegisUrl + 'artikel/' + filename
      }
    } catch (e) {
      /* fallback if string is not JSON */
    }
    return '/images/icon/NoData.png'
  }

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { scrollLeft, clientWidth } = scrollContainerRef.current
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth * 0.8 : scrollLeft + clientWidth * 0.8
      scrollContainerRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' })
    }
  }

  const allCategories = [{ ID_KATEGORI: 'all', KATEGORI: 'Semua', SLUG: 'all' }, ...categories]

  return (
    <Box component='section' sx={{ py: { xs: 8, md: 12 }, bgcolor: WARM_BG }}>
      <Container maxWidth='lg'>
        {/* ── Section Header ── */}
        <Box sx={{ mb: { xs: 5, md: 6 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.2,
              px: 2,
              py: 0.75,
              bgcolor: BRAND_PILL_BG,
              borderRadius: '100px',
              mb: 2.5
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: BRAND
              }}
            />
            <Typography
              sx={{
                fontSize: '0.725rem',
                fontWeight: 800,
                color: BRAND,
                letterSpacing: '0.12em',
                textTransform: 'uppercase'
              }}
            >
              Artikel & Berita
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 3
            }}
          >
            <Box sx={{ maxWidth: 640 }}>
              <Typography
                variant='h2'
                component='h2'
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.85rem', md: '2.75rem' },
                  color: CHARCOAL,
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  mb: 2
                }}
              >
                Informasi & Panduan Kedukaan
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: MUTED_BROWN,
                  lineHeight: 1.7
                }}
              >
                Artikel terpilih seputar panduan kedukaan, tips kesehatan keluarga, serta berita terkini layanan
                persemayaman dari kami.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              {/* Horizontal Scroll Helpers (Desktop & Mobile) */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                <IconButton
                  onClick={() => scroll('left')}
                  sx={{
                    border: '1.5px solid #dcd5c9',
                    color: BRAND,
                    '&:hover': { bgcolor: BRAND_LIGHT, borderColor: BRAND }
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  onClick={() => scroll('right')}
                  sx={{
                    border: '1.5px solid #dcd5c9',
                    color: BRAND,
                    '&:hover': { bgcolor: BRAND_LIGHT, borderColor: BRAND }
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              <Link href='/artikel' passHref style={{ textDecoration: 'none' }}>
                <Button
                  variant='outlined'
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important', transition: 'transform 0.2s' }} />}
                  sx={{
                    borderColor: BRAND,
                    color: BRAND,
                    borderRadius: '100px',
                    px: 3.5,
                    py: 1.25,
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: BRAND,
                      borderColor: BRAND,
                      color: '#ffffff',
                      '& .MuiButton-endIcon svg': {
                        transform: 'translateX(4px)'
                      }
                    }
                  }}
                >
                  Lihat Semua Artikel
                </Button>
              </Link>
            </Box>
          </Box>
        </Box>

        {/* ── Category Horizontal Tabs ── */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.2,
            mb: { xs: 4, md: 6 },
            overflowX: 'auto',
            pb: 1.5,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {allCategories.map(cat => {
            const isActive = selectedCategory === cat.ID_KATEGORI
            return (
              <Box
                key={cat.ID_KATEGORI}
                component='button'
                onClick={() => setSelectedCategory(cat.ID_KATEGORI)}
                sx={{
                  flexShrink: 0,
                  px: 3,
                  py: 1.25,
                  borderRadius: '100px',
                  border: '1.5px solid',
                  borderColor: isActive ? BRAND : '#e6dfd5',
                  bgcolor: isActive ? BRAND : '#ffffff',
                  color: isActive ? '#ffffff' : MUTED_BROWN,
                  fontSize: '0.85rem',
                  fontWeight: isActive ? 700 : 600,
                  cursor: 'pointer',
                  boxShadow: isActive ? '0 4px 12px rgba(115, 86, 38, 0.15)' : '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: 'inherit',
                  lineHeight: 1.5,
                  '&:hover': {
                    borderColor: BRAND,
                    color: isActive ? '#ffffff' : BRAND,
                    bgcolor: isActive ? BRAND_HOVER : BRAND_LIGHT
                  }
                }}
              >
                {cat.KATEGORI}
              </Box>
            )
          })}
        </Box>

        {/* ── Articles List / Cards Layout ── */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map(n => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 4 }}>
                  <Skeleton variant='rounded' height={220} sx={{ borderRadius: 3, mb: 2 }} />
                  <Skeleton variant='text' width='85%' height={24} sx={{ mb: 1 }} />
                  <Skeleton variant='text' width='60%' height={20} sx={{ mb: 2 }} />
                  <Skeleton variant='text' width='40%' height={18} />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : articles.length === 0 ? (
          <Box
            sx={{
              py: 12,
              textAlign: 'center',
              bgcolor: '#ffffff',
              borderRadius: 4,
              border: '1.5px dashed #ded6cc',
              boxShadow: '0 4px 20px rgba(0,0,0,0.01)'
            }}
          >
            <Typography sx={{ color: MUTED_BROWN, fontSize: '1rem', fontWeight: 500 }}>
              Tidak ada artikel di kategori ini.
            </Typography>
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              gap: { xs: 2.5, md: 3 },
              overflowX: 'auto',
              pb: 3,
              pt: 1,
              scrollbarWidth: 'none',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { display: 'none' },
              // On desktop, render as a nice grid wrap or allow horizontal scroll
              flexWrap: { xs: 'nowrap', md: 'nowrap' }
            }}
          >
            {articles.map((article, idx) => {
              let categorySlug = article.SLUG_KATEGORI
              if (!categorySlug && article.ID_KATEGORI) {
                const matched = categories.find(c => String(c.ID_KATEGORI) === String(article.ID_KATEGORI))
                if (matched) categorySlug = matched.SLUG
              }
              if (!categorySlug) categorySlug = 'uncategorized'
              const articleUrl = `/${categorySlug}/${article.SLUG}`

              return (
                <Box
                  key={article.ID_ARTIKEL}
                  sx={{
                    flexShrink: 0,
                    width: { xs: '290px', sm: '340px', md: '370px' },
                    scrollSnapAlign: 'start',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Link href={articleUrl} passHref style={{ textDecoration: 'none' }}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        bgcolor: '#ffffff',
                        border: '1px solid #eae2d5',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 15px rgba(115, 86, 38, 0.03)',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: '0 16px 36px rgba(115, 86, 38, 0.12)',
                          borderColor: alpha(BRAND, 0.35),
                          '& .article-card-img': { transform: 'scale(1.05)' },
                          '& .read-more-btn': { color: BRAND_HOVER, gap: '8px' }
                        }
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          aspectRatio: '16/10',
                          bgcolor: BRAND_LIGHT
                        }}
                      >
                        <Box
                          className='article-card-img'
                          component='img'
                          src={getArticleImageUrl(article.GAMBAR)}
                          alt={article.JUDUL}
                          onError={(e: any) => {
                            e.target.src = '/images/icon/NoData.png'
                          }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            display: 'block'
                          }}
                        />
                        {article.NAMA_KATEGORI && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 16,
                              left: 16,
                              px: 1.5,
                              py: 0.5,
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                              backdropFilter: 'blur(4px)',
                              borderRadius: '50px',
                              border: '1px solid rgba(115, 86, 38, 0.1)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.675rem',
                                fontWeight: 800,
                                color: BRAND,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}
                            >
                              {article.NAMA_KATEGORI}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <CardContent
                        sx={{
                          p: 3,
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: 2
                        }}
                      >
                        <Box>
                          <Typography
                            variant='h3'
                            component='h3'
                            sx={{
                              fontWeight: 800,
                              fontSize: '1.125rem',
                              color: CHARCOAL,
                              lineHeight: 1.45,
                              mb: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontFamily: 'inherit'
                            }}
                          >
                            {article.JUDUL}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: MUTED_BROWN,
                              lineHeight: 1.6,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 1
                            }}
                          >
                            {article.EXERPT ||
                              'Klik untuk membaca panduan lengkap dan rincian selengkapnya tentang artikel ini.'}
                          </Typography>
                        </Box>

                        <Box
                          className='read-more-btn'
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            color: BRAND,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            transition: 'all 0.2s ease',
                            mt: 1
                          }}
                        >
                          Baca Selengkapnya
                          <ArrowForwardIcon sx={{ fontSize: 14 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Link>
                </Box>
              )
            })}
          </Box>
        )}
      </Container>
    </Box>
  )
}
